// apps/web/src/components/social-accounts/InstagramLink.tsx
//
// Instagram Professional account link/unlink UI. Lives next to the
// generic SocialAccountsManager. The flow is server-driven:
//   1. User clicks "Link Instagram".
//   2. We POST /api/v1/instagram/connections with the creator id
//      and the current location, get back { authUrl }.
//   3. window.location = authUrl → Meta → /api/v1/instagram/callback
//      → redirect back to the same page with ?instagram=connected&username=….
//   4. We re-fetch the connections list.
//
// The token itself never reaches the client; the server stores the
// long-lived token encrypted at rest. We just display the
// username / followers / expiry and offer refresh + unlink.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiDelete, apiGet, apiPost, type ApiResponse } from '../../lib/apiClient.js';

interface InstagramConnection {
  id: string;
  socialAccountId: string;
  creatorProfileId: string;
  tenantId: string;
  username: string;
  accountType: string;
  followerCount: number | null;
  followsCount: number | null;
  mediaCount: number | null;
  profilePictureUrl: string | null;
  tokenExpiresAt: string;
  tokenRefreshedAt: string | null;
  lastSyncedAt: string | null;
  status: string;
  scopes: string[];
}

interface ConnectionsResponse {
  connections: InstagramConnection[];
}

interface ConfigResponse {
  enabled: boolean;
  scopes: string[];
  redirectUri: string | null;
}

interface CachedMedia {
  id: string;
  media_id: string;
  media_type: string | null;
  caption: string | null;
  permalink: string | null;
  media_url: string | null;
  thumbnail_url: string | null;
  timestamp: string | null;
  like_count: number | null;
  comments_count: number | null;
  fetched_at: string;
}

interface InsightsResponse {
  period: string;
  metrics: { name: string; period: string; values: { value: number; end_time?: string }[] }[];
  metricsError: string | null;
  topPosts: {
    id: string;
    mediaId: string;
    type: string | null;
    caption: string | null;
    permalink: string | null;
    mediaUrl: string | null;
    thumbnailUrl: string | null;
    timestamp: string | null;
    likeCount: number | null;
    commentsCount: number | null;
    engagement: number;
  }[];
  averageLikes: number;
  averageComments: number;
  sampleSize: number;
}

interface CreatorSummary {
  id: string;
  displayName?: string;
}

interface Props {
  creatorId: string;
  creatorDisplayName?: string;
}

function formatExpiry(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return 'unknown';
  const days = Math.max(0, Math.round((ms - Date.now()) / 86_400_000));
  if (days === 0) return 'expires today';
  if (days === 1) return 'expires tomorrow';
  return `expires in ${days} days`;
}

export function InstagramLink({ creatorId, creatorDisplayName }: Props) {
  const [params, setParams] = useSearchParams();
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [list, setList] = useState<ConnectionsResponse | null>(null);
  const [media, setMedia] = useState<CachedMedia[] | null>(null);
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mine = (list?.connections ?? []).filter((c) => c.creatorProfileId === creatorId);

  const refresh = async () => {
    setError(null);
    try {
      const [cfg, conns] = await Promise.all([
        apiGet<ConfigResponse>('/api/v1/instagram/config'),
        apiGet<ConnectionsResponse>('/api/v1/instagram/connections'),
      ]);
      setConfig(cfg.data);
      setList(conns.data);
      if (mine.length > 0) {
        const mediaRes = await apiGet<{ media: CachedMedia[] }>(`/api/v1/instagram/connections/${mine[0]!.id}/media?limit=12`);
        setMedia(mediaRes.data.media);
      } else {
        setMedia(null);
      }
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Could not load Instagram status.');
    }
  };

  const loadInsights = async (id: string) => {
    try {
      const res = await apiGet<InsightsResponse>(`/api/v1/instagram/connections/${id}/insights?period=day`);
      setInsights(res.data);
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Could not load insights.');
    }
  };

  useEffect(() => {
    void refresh();
    if (params.get('instagram') === 'connected') {
      setError(null);
      const newParams = new URLSearchParams(params);
      newParams.delete('instagram');
      newParams.delete('username');
      newParams.delete('instagram_error');
      setParams(newParams, { replace: true });
    }
    if (params.get('instagram_error')) {
      setError(`Instagram returned: ${params.get('instagram_error')}`);
      const newParams = new URLSearchParams(params);
      newParams.delete('instagram_error');
      setParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId]);

  const link = async () => {
    setWorking('link');
    setError(null);
    try {
      const res = await apiPost<{ authUrl: string }>('/api/v1/instagram/connections', {
        creatorProfileId: creatorId,
        returnTo: window.location.pathname + window.location.search,
      });
      window.location.assign(res.data.authUrl);
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Could not start Instagram link.');
      setWorking(null);
    }
  };

  const refreshOne = async (id: string) => {
    setWorking(id);
    setError(null);
    try {
      await apiPost(`/api/v1/instagram/connections/${id}/refresh`);
      await apiPost(`/api/v1/instagram/connections/${id}/sync`);
      await refresh();
      await loadInsights(id);
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Could not refresh Instagram data.');
    } finally {
      setWorking(null);
    }
  };

  const unlink = async (id: string) => {
    if (!window.confirm('Disconnect this Instagram account? Cached media and tokens will be deleted.')) return;
    setWorking(id);
    setError(null);
    try {
      await apiDelete(`/api/v1/instagram/connections/${id}`);
      await refresh();
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Could not unlink Instagram.');
    } finally {
      setWorking(null);
    }
  };

  if (config && !config.enabled) {
    return (
      <section className="manager-card form-card">
        <h3>Instagram</h3>
        <p className="muted">Instagram linking is not configured on this server. Set <code>INSTAGRAM_APP_ID</code> + <code>INSTAGRAM_APP_SECRET</code> + <code>INSTAGRAM_TOKEN_ENCRYPTION_KEY</code> in the API Worker to enable it.</p>
      </section>
    );
  }

  return (
    <section className="manager-card form-card instagram-link">
      <h3>Instagram Professional account</h3>
      {creatorDisplayName && <p className="muted">For creator profile: {creatorDisplayName}</p>}
      {error && <p className="notice error">{error}</p>}
      {mine.length === 0 ? (
        <>
          <p>Link a Professional (Business or Creator) Instagram account to fetch your recent media, follower count, and engagement metrics. Personal accounts are not supported by the Graph API.</p>
          <p className="muted">Scopes requested: {config?.scopes.join(', ')}</p>
          <div className="actions">
            <button type="button" className="btn btn-primary" disabled={working === 'link'} onClick={() => void link()}>
              {working === 'link' ? 'Opening Instagram…' : 'Link Instagram'}
            </button>
          </div>
        </>
      ) : (
        <>
          {mine.map((conn) => (
            <div className="instagram-connection" key={conn.id}>
              <div className="instagram-header">
                {conn.profilePictureUrl && <img src={conn.profilePictureUrl} alt="" className="instagram-avatar" />}
                <div>
                  <p><strong>@{conn.username}</strong> <span className="status-badge">{conn.accountType}</span></p>
                  <p className="muted">
                    {conn.followerCount != null ? `${conn.followerCount.toLocaleString()} followers · ` : ''}
                    {conn.mediaCount != null ? `${conn.mediaCount} posts · ` : ''}
                    {formatExpiry(conn.tokenExpiresAt)}
                  </p>
                </div>
              </div>
              <div className="actions">
                <button type="button" className="btn btn-secondary" disabled={working === conn.id} onClick={() => void refreshOne(conn.id)}>
                  {working === conn.id ? 'Refreshing…' : 'Refresh data'}
                </button>
                <button type="button" className="btn btn-secondary" disabled={Boolean(insights && working !== null)} onClick={() => void loadInsights(conn.id)}>
                  Load insights
                </button>
                <button type="button" className="btn btn-danger" disabled={working === conn.id} onClick={() => void unlink(conn.id)}>
                  Unlink
                </button>
              </div>
              {insights && (
                <div className="instagram-insights">
                  {insights.metricsError && <p className="muted">Live insights unavailable: {insights.metricsError}</p>}
                  {insights.metrics.length > 0 && (
                    <div className="instagram-metric-grid">
                      {insights.metrics.map((m) => {
                        const latest = m.values[m.values.length - 1]?.value;
                        return (
                          <div key={m.name} className="instagram-metric">
                            <div className="instagram-metric-name">{m.name.replace(/_/g, ' ')}</div>
                            <div className="instagram-metric-value">{latest != null ? latest.toLocaleString() : '—'}</div>
                            <div className="muted">{m.period}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="instagram-averages">
                    <span>Avg ♥ {insights.averageLikes.toLocaleString()}</span>
                    <span>Avg 💬 {insights.averageComments.toLocaleString()}</span>
                    <span className="muted">over {insights.sampleSize} cached posts</span>
                  </div>
                  {insights.topPosts.length > 0 && (
                    <div className="instagram-top-posts">
                      <h4>Top performing</h4>
                      <ol>
                        {insights.topPosts.map((p) => (
                          <li key={p.id}>
                            <a href={p.permalink ?? '#'} target="_blank" rel="noopener noreferrer">
                              {(p.thumbnailUrl || p.mediaUrl) ? <img src={(p.thumbnailUrl ?? p.mediaUrl)!} alt="" loading="lazy" /> : <div className="instagram-public-placeholder">{p.type ?? 'media'}</div>}
                              <span>♥ {p.likeCount ?? '—'} · 💬 {p.commentsCount ?? '—'}</span>
                            </a>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
              {media && media.length > 0 && (
                <div className="instagram-media-grid">
                  {media.map((m) => (
                    <a key={m.id} href={m.permalink ?? '#'} target="_blank" rel="noopener noreferrer" className="instagram-media-tile">
                      {m.thumbnail_url || m.media_url ? <img src={(m.thumbnail_url ?? m.media_url)!} alt="" loading="lazy" /> : <div className="instagram-media-placeholder">{m.media_type ?? 'media'}</div>}
                      <div className="instagram-media-meta">
                        <span>♥ {m.like_count ?? '—'}</span>
                        <span>💬 {m.comments_count ?? '—'}</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
              {media && media.length === 0 && <p className="muted">No cached media yet — click &ldquo;Refresh data&rdquo; to fetch.</p>}
            </div>
          ))}
        </>
      )}
    </section>
  );
}

export type { InstagramConnection, CreatorSummary, CachedMedia, ApiResponse };
